import React, { useEffect, useMemo, useRef, useState } from "react";

// Simple themed dropdown to replace native <select> so the list matches the app theme
// Props: value, onChange(newValue), options (array of { label, value } or strings), placeholder, disabled, className, searchable, size, variant
const ThemedSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled = false,
  className = "",
  searchable = false,
  size = "large",
  variant = "themed",
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [panelRect, setPanelRect] = useState({ top: 0, left: 0, width: 0, maxHeight: 256 });

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) =>
      typeof opt === "string" ? { label: opt, value: opt } : { label: opt.label, value: String(opt.value) }
    );
  }, [options]);

  const selected = useMemo(() => normalizedOptions.find((o) => String(o.value) === String(value)), [normalizedOptions, value]);
  const filteredOptions = useMemo(() => {
    if (!searchable) return normalizedOptions;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [normalizedOptions, searchable, searchTerm]);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
    };
    const updatePanelRect = () => {
      if (!open || !buttonRef.current) return;
      const r = buttonRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const spaceBelow = viewportH - r.bottom;
      const spaceAbove = r.top;
      const desired = 360; // px desired panel height
      const pad = 8;
      // Decide placement side first
      const placeBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
      // Constrain height to available space on chosen side
      let available = placeBelow ? (spaceBelow - pad) : (spaceAbove - pad);
      if (!isFinite(available) || available <= 0) available = 200;
      let maxHeight = Math.min(desired, available);
      if (maxHeight < 160) maxHeight = Math.max(available, 160);
      // Compute top/left in viewport coordinates for position: fixed (no scroll offsets)
      let top = placeBelow ? (r.bottom + pad / 2) : (r.top - maxHeight - pad / 2);
      // Clamp within viewport
      if (!placeBelow) top = Math.max(pad, top);
      // Ensure left stays in viewport; adjust if near right edge
      let left = r.left;
      if (left + r.width > viewportW - pad) {
        left = Math.max(pad, viewportW - pad - r.width);
      }
      setPanelRect({ top, left, width: r.width, maxHeight });
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", updatePanelRect);
    window.addEventListener("scroll", updatePanelRect, true);
    // initial compute when opening
    updatePanelRect();
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", updatePanelRect);
      window.removeEventListener("scroll", updatePanelRect, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      // Ensure selected is visible
      const idx = filteredOptions.findIndex((o) => String(o.value) === String(value));
      if (idx >= 0) {
        const item = listRef.current.querySelector(`[data-option-index="${idx}"]`);
        if (item && item.scrollIntoView) item.scrollIntoView({ block: "nearest" });
      }
    }
  }, [open, filteredOptions, value]);

  const handleSelect = (v) => {
    onChange && onChange(v);
    setOpen(false);
    if (buttonRef.current) buttonRef.current.focus();
  };

  const buttonSizeClass = size === "medium" ? "h-10 text-base" : "h-12 text-lg";
  const isPlain = variant === "plain";
  const buttonToneClass = isPlain
    ? "border-slate-300 bg-white text-slate-900 focus:ring-primary focus:border-primary"
    : "border-accent/40 bg-accent/10 text-primary focus:ring-accent focus:border-accent";
  const panelToneClass = isPlain
    ? "border-slate-300 bg-white"
    : "border-accent/40 bg-secondary";
  const searchToneClass = isPlain
    ? "border-slate-300 bg-white text-slate-900 focus:ring-primary"
    : "border-accent/40 bg-white text-primary focus:ring-accent";

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((s) => !s)}
        className={`w-full text-left px-4 border rounded focus:outline-none focus:ring-2 ${buttonToneClass} ${buttonSizeClass} ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {selected ? selected.label : (
          <span className="text-gray-500">{placeholder}</span>
        )}
        <span className="absolute right-3 inset-y-0 flex items-center text-primary pointer-events-none">
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
          </svg>
        </span>
      </button>
      {open && (
        <div
          ref={listRef}
          style={{ position: "fixed", top: panelRect.top, left: panelRect.left, width: panelRect.width, maxHeight: panelRect.maxHeight }}
          className={`z-50 overflow-auto rounded border shadow-lg ${panelToneClass}`}
          role="listbox"
        >
          {searchable ? (
            <div className={`sticky top-0 z-10 p-2 border-b ${isPlain ? "border-slate-200 bg-white" : "border-accent/30 bg-secondary"}`}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className={`w-full h-10 px-3 border rounded focus:outline-none focus:ring-2 ${searchToneClass}`}
                autoFocus
              />
            </div>
          ) : null}
          {filteredOptions.map((opt, index) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <div
                key={opt.value}
                data-option-index={index}
                onClick={() => handleSelect(opt.value)}
                className={`px-4 py-2 cursor-pointer ${
                  isPlain
                    ? isSelected
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-900 hover:bg-slate-50"
                    : isSelected
                    ? "bg-accent/40 text-primary"
                    : "text-primary hover:bg-accent/20"
                }`}
                role="option"
                aria-selected={isSelected}
              >
                {opt.label}
              </div>
            );
          })}
          {filteredOptions.length === 0 && (
            <div className="px-4 py-2 text-gray-500">No options</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThemedSelect;


